import React, { useContext, useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { JSONRecordData } from '../Log/graph.util';
import {
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom';
import Button from '@mui/material/Button';
import { FormattedMessage } from 'react-intl';
import { I_S3Credentials } from '../../../interfaces/S3Types';
import routes from '../../../Resources/routes.json';
import CircularProgress from '@mui/material/CircularProgress';
import MainStats from './MainStats';
import DetailedBatteryView from './DetailedBatteryView';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';
import DetailedBatteryViewSodistore from './DetailedBatteryViewSodistore';

interface BatteryViewProps {
  values: JSONRecordData;
  s3Credentials: I_S3Credentials;
  installationId: number;
  connected: boolean;
}

function BatteryView(props: BatteryViewProps) {
  if (props.values === null && props.connected == true) {
    return null;
  }

  const currentLocation = useLocation();
  const navigate = useNavigate();
  const { product, setProduct } = useContext(ProductIdContext);

  // SodiStoreGrid (product 4) keeps batteries under ListOfBatteriesRecord[cluster].Devices[id].
  // Flatten into a single list with composite IDs "{cluster}-{device}" so the existing
  // BatteryView table renders without further changes.
  const sortedBatteryView = (() => {
    if (props.values == null) return [];

    if (
      product === 4 &&
      props.values.ListOfBatteriesRecord
    ) {
      const flat: { BatteryId: string; battery: any }[] = [];
      Object.entries(props.values.ListOfBatteriesRecord).forEach(
        ([clusterId, cluster]: [string, any]) => {
          if (cluster?.Devices) {
            Object.entries(cluster.Devices).forEach(([devId, dev]) => {
              flat.push({ BatteryId: `${clusterId}-${devId}`, battery: dev });
            });
          }
        }
      );
      return flat.sort((a, b) => a.BatteryId.localeCompare(b.BatteryId));
    }

    if (props.values?.Battery?.Devices) {
      return Object.entries(props.values.Battery.Devices)
        .map(([BatteryId, battery]) => ({ BatteryId, battery }))
        .sort((a, b) => parseInt(b.BatteryId) - parseInt(a.BatteryId));
    }
    return [];
  })();

  const [loading, setLoading] = useState(sortedBatteryView.length == 0);

  const handleMainStatsButton = () => {
    navigate(routes.mainstats);
  };

  useEffect(() => {
    if (sortedBatteryView.length == 0) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [sortedBatteryView]);

  return (
    <>
      {!props.connected && (
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70vh'
          }}
        >
          <CircularProgress size={60} style={{ color: '#ffc04d' }} />
          <Typography
            variant="body2"
            style={{ color: 'black', fontWeight: 'bold' }}
            mt={2}
          >
            <FormattedMessage id="unableToCommunicate" defaultMessage="Unable to communicate with the installation" />
          </Typography>
          <Typography variant="body2" style={{ color: 'black' }}>
            <FormattedMessage id="pleaseWaitOrRefresh" defaultMessage="Please wait or refresh the page" />
          </Typography>
        </Container>
      )}
      {loading && props.connected && (
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70vh'
          }}
        >
          <CircularProgress size={60} style={{ color: '#ffc04d' }} />
          <Typography
            variant="body2"
            style={{ color: 'black', fontWeight: 'bold' }}
            mt={2}
          >
            <FormattedMessage id="batteryServiceNotAvailable" defaultMessage="Battery service is not available at the moment" />
          </Typography>
          <Typography variant="body2" style={{ color: 'black' }}>
            <FormattedMessage id="pleaseWaitOrRefresh" defaultMessage="Please wait or refresh the page" />
          </Typography>
        </Container>
      )}

      {!loading && props.connected && (
        <Container maxWidth="xl">
          <Grid container>
            <Grid
              item
              xs={6}
              md={6}
              sx={{
                display:
                  !currentLocation.pathname.includes('detailed_view') &&
                  !currentLocation.pathname.includes('mainstats')
                    ? 'block'
                    : 'none'
              }}
            >
              <Button
                variant="contained"
                sx={{
                  marginTop: '20px',
                  backgroundColor: '#808080',
                  color: '#000000',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
              >
                <FormattedMessage
                  id="battery_view"
                  defaultMessage="Battery View"
                />
              </Button>

              <Button
                variant="contained"
                onClick={handleMainStatsButton}
                sx={{
                  marginTop: '20px',
                  marginLeft: '20px',
                  backgroundColor: '#ffc04d',
                  color: '#000000',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
              >
                <FormattedMessage id="main_stats" defaultMessage="Main Stats" />
              </Button>
            </Grid>
          </Grid>

          <Grid container>
            <Routes>
              <Route
                path={routes.mainstats + '/*'}
                element={
                  <MainStats
                    s3Credentials={props.s3Credentials}
                    id={props.installationId}
                  ></MainStats>
                }
              />
              {product === 0
                ? sortedBatteryView.map(({ BatteryId, battery }) => (
                    <Route
                      key={routes.detailed_view + BatteryId}
                      path={routes.detailed_view + BatteryId}
                      element={
                        <DetailedBatteryView
                          batteryId={Number(BatteryId)}
                          s3Credentials={props.s3Credentials}
                          batteryData={battery}
                          installationId={props.installationId}
                          productNum={product}
                        ></DetailedBatteryView>
                      }
                    />
                  ))
                : sortedBatteryView.map(({ BatteryId, battery }) => (
                    <Route
                      key={routes.detailed_view + BatteryId}
                      path={routes.detailed_view + BatteryId}
                      element={
                        <DetailedBatteryViewSodistore
                          // Keep BatteryId as-is (Number("1-1") === NaN for product 4).
                          batteryId={BatteryId}
                          s3Credentials={props.s3Credentials}
                          batteryData={battery}
                          installationId={props.installationId}
                          productNum={product}
                        ></DetailedBatteryViewSodistore>
                      }
                    />
                  )
                  )}
            </Routes>
          </Grid>

          <TableContainer
            component={Paper}
            sx={{
              marginTop: '20px',
              marginBottom: '20px',
              display:
                !currentLocation.pathname.includes('detailed_view') &&
                !currentLocation.pathname.includes('mainstats')
                  ? 'block'
                  : 'none'
            }}
          >
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="center"><FormattedMessage id="battery" defaultMessage="Battery" /></TableCell>
                  <TableCell align="center"><FormattedMessage id="firmware" defaultMessage="Firmware" /></TableCell>
                  <TableCell align="center"><FormattedMessage id="power" defaultMessage="Power" /></TableCell>
                  <TableCell align="center"><FormattedMessage id="batteryVoltage" defaultMessage="Battery Voltage" /></TableCell>
                  <TableCell align="center"><FormattedMessage id="soc" defaultMessage="SoC" /></TableCell>
                  <TableCell align="center"><FormattedMessage id="temperature" defaultMessage="Temperature" /></TableCell>
                  {product === 0 ? (
                    <TableCell align="center"><FormattedMessage id="warnings" defaultMessage="Warnings" /></TableCell>
                  ) : (
                    <TableCell align="center"><FormattedMessage id="minCellVoltage" defaultMessage="Min Cell Voltage" /></TableCell>
                  )}
                  {product === 0 ? (
                    <TableCell align="center"><FormattedMessage id="alarms" defaultMessage="Alarms" /></TableCell>
                  ) : (
                    <TableCell align="center"><FormattedMessage id="maxCellVoltage" defaultMessage="Max Cell Voltage" /></TableCell>
                  )}
                  {(product === 3 || product === 4) && (
                    <TableCell align="center"><FormattedMessage id="voltageDifference" defaultMessage="Voltage Difference" /></TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedBatteryView.map(({ BatteryId, battery }) => (
                  <TableRow
                    key={BatteryId}
                    style={{
                      height: '10px'
                    }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      align="center"
                      sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', minWidth: '100px' }}
                    >
                      <Link
                        style={{ color: 'black' }}
                        to={routes.detailed_view + BatteryId}
                      >
                        {'Node ' + BatteryId}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '10%',
                        textAlign: 'center'
                      }}
                    >
                      {product == 0
                        ? battery.FwVersion
                        : battery.BatteryDeligreenDataRecord.FwVersion + '.0'}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '10%',
                        textAlign: 'center'
                      }}
                    >
                      {product == 0
                        ? battery.Dc.Power + ' ' + 'W'
                        : battery.BatteryDeligreenDataRecord.Power + ' ' + 'W'}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '14%',
                        textAlign: 'center',

                        backgroundColor:
                          product == 0
                            ? battery.Dc.Voltage < 44 || battery.Dc.Voltage > 57
                              ? '#FF033E'
                              : '#32CD32'
                            : battery.BatteryDeligreenDataRecord
                                .TotalBatteryVoltage < 32 ||
                              battery.BatteryDeligreenDataRecord
                                .TotalBatteryVoltage > 63
                            ? '#FF033E'
                            : '#32CD32',
                        color:
                          product === 0
                            ? battery.Dc.Voltage
                              ? 'black'
                              : 'white'
                            : battery.BatteryDeligreenDataRecord
                                .TotalBatteryVoltage
                            ? 'black'
                            : 'white'
                      }}
                    >
                      {product === 0
                        ? battery.Dc.Voltage + ' ' + 'V'
                        : battery.BatteryDeligreenDataRecord
                            .TotalBatteryVoltage +
                          ' ' +
                          'V'}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '10%',
                        textAlign: 'center',
                        backgroundColor:
                          product === 0
                            ? battery.Soc < 20
                              ? '#FF033E'
                              : battery.Soc < 50
                              ? '#ffbf00'
                              : '#32CD32'
                            : battery.BatteryDeligreenDataRecord.Soc <= 10
                            ? '#FF033E'
                            : battery.BatteryDeligreenDataRecord.Soc < 50
                            ? '#ffbf00'
                            : '#32CD32',
                        color:
                          product === 0
                            ? battery.Soc
                              ? 'inherit'
                              : 'white'
                            : battery.BatteryDeligreenDataRecord.Soc
                            ? 'inherit'
                            : 'white'
                      }}
                    >
                      {product == 0
                        ? battery.Soc + ' %'
                        : battery.BatteryDeligreenDataRecord.Soc + ' %'}
                    </TableCell>
                    <TableCell
                      sx={() => {
                        if (product === 0) {
                          const avg = battery.Temperatures.Cells.Average;
                          return {
                            width: '10%',
                            textAlign: 'center',
                            backgroundColor:
                              avg > 300
                                ? '#FF033E'
                                : avg > 280
                                ? '#ffbf00'
                                : avg < 245
                                ? '#008FFB'
                                : '#32CD32'
                          };
                        } else {
                          const avgTemp =
                            (battery.BatteryDeligreenDataRecord.TemperaturesList
                              .CellTemperature1 +
                              battery.BatteryDeligreenDataRecord
                                .TemperaturesList.CellTemperature2 +
                              battery.BatteryDeligreenDataRecord
                                .TemperaturesList.CellTemperature3 +
                              battery.BatteryDeligreenDataRecord
                                .TemperaturesList.CellTemperature4) /
                            4;
                          return {
                            width: '10%',
                            textAlign: 'center',
                            backgroundColor:
                              avgTemp > 50 || avgTemp < 0
                                ? '#FF033E'
                                : '#32CD32'
                          };
                        }
                      }}
                    >
                      {product === 0
                        ? battery.Temperatures.Cells.Average + ' °C'
                        : battery.BatteryDeligreenDataRecord.TemperaturesList
                            .EnvironmentTemperature + ' °C'}
                    </TableCell>

                    {product === 0 && (
                      <>
                        <TableCell
                          style={{
                            width: '20%',
                            textAlign: 'center',
                            padding: '8px',
                            fontWeight:
                              battery.Warnings !== '' ? 'bold' : 'inherit',
                            backgroundColor:
                              battery.Warnings === '' ? 'inherit' : '#ff9900',
                            color: battery.Warnings !== '' ? 'black' : 'inherit'
                          }}
                        >
                          {battery.Warnings === '' ? (
                            'None'
                          ) : battery.Warnings.toString().split('-').length >
                            1 ? (
                            <Link
                              style={{ color: 'black' }}
                              to={
                                currentLocation.pathname.substring(
                                  0,
                                  currentLocation.pathname.lastIndexOf('/') + 1
                                ) +
                                routes.log +
                                '?open=warning'
                              }
                            >
                              Multiple Warnings
                            </Link>
                          ) : (
                            battery.Warnings
                          )}
                        </TableCell>

                        <TableCell
                          sx={{
                            width: '20%',
                            textAlign: 'center',
                            fontWeight:
                              battery.Alarms !== '' ? 'bold' : 'inherit',
                            backgroundColor:
                              battery.Alarms === '' ? 'inherit' : '#FF033E',
                            color: battery.Alarms !== '' ? 'black' : 'inherit'
                          }}
                        >
                          {battery.Alarms === '' ? (
                            'None'
                          ) : battery.Alarms.toString().split('-').length >
                            1 ? (
                            <Link
                              style={{ color: 'black' }}
                              to={
                                currentLocation.pathname.substring(
                                  0,
                                  currentLocation.pathname.lastIndexOf('/') + 1
                                ) +
                                routes.log +
                                '?open=error'
                              }
                            >
                              Multiple Alarms
                            </Link>
                          ) : (
                            battery.Alarms
                          )}
                        </TableCell>
                      </>
                    )}
                    {(product === 3 || product === 4) && (
                      <>
                        {(() => {
                          const cellVoltagesString =
                            battery.BatteryDeligreenDataRecord.CellVoltage ||
                            '';

                          const cellVoltagesArray: number[] = cellVoltagesString
                            .split(',')
                            .map((v) => parseFloat(v.trim()))
                            .filter((v) => !isNaN(v)); // Filter invalid numbers

                          const minVoltage = Math.min(...cellVoltagesArray);
                          const maxVoltage = Math.max(...cellVoltagesArray);

                          return (
                            <>
                              <TableCell
                                sx={{
                                  width: '15%',
                                  textAlign: 'center',
                                  padding: '8px',
                                  backgroundColor:
                                    minVoltage < 2
                                      ? '#FF033E'
                                      : minVoltage > 2 && minVoltage < 2.2
                                      ? '#ffbf00'
                                      : '#32CD32',
                                  color: minVoltage != 0 ? 'black' : 'white'
                                }}
                              >
                                {minVoltage.toFixed(3)} V{' '}
                              </TableCell>

                              <TableCell
                                sx={{
                                  width: '15%',
                                  textAlign: 'center',
                                  padding: '8px',
                                  backgroundColor:
                                    maxVoltage > 3.9
                                      ? '#FF033E'
                                      : maxVoltage < 3.9 && maxVoltage > 3.8
                                      ? '#ffbf00'
                                      : '#32CD32',
                                  color: maxVoltage != 0 ? 'black' : 'white'
                                }}
                              >
                                {maxVoltage.toFixed(3)} V{' '}
                              </TableCell>
                            </>
                          );
                        })()}
                      </>
                    )}
                    {(product === 3 || product === 4) && (
                      <>
                        {(() => {
                          const cellVoltagesString =
                            battery.BatteryDeligreenDataRecord.CellVoltage ||
                            '';

                          const cellVoltagesArray: number[] = cellVoltagesString
                            .split(',')
                            .map((v) => parseFloat(v.trim()))
                            .filter((v) => !isNaN(v)); // Filter invalid numbers

                          const minVoltage = Math.min(...cellVoltagesArray);
                          const maxVoltage = Math.max(...cellVoltagesArray);

                          const voltage_diff = maxVoltage - minVoltage;

                          return (
                            <>
                              <TableCell
                                sx={{
                                  width: '15%',
                                  textAlign: 'center',
                                  padding: '8px',
                                  backgroundColor:
                                    voltage_diff > 0.2
                                      ? '#FF033E'
                                      : voltage_diff > 0.1 && voltage_diff < 0.2
                                      ? '#ffbf00'
                                      : '#32CD32',
                                  color: voltage_diff != 0 ? 'black' : 'white'
                                }}
                              >
                                {voltage_diff.toFixed(3)} V{' '}
                              </TableCell>
                            </>
                          );
                        })()}
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      )}
    </>
  );
}

export default BatteryView;
